import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SfoWidgetModule } from 'projects/ui-components/src/public-api';
import { Reference } from '../../reference.model';
import { ReferenceEditorComponent } from '../reference-editor/reference-editor.component';
import type { CSLReferenceMeta } from '@sciflow/cite';

export interface BibliographyGeneratorDialogData {
  reference: Reference;
  exportFormat: 'auto' | 'custom';
  canEdit: boolean;
  isPlainReference: boolean;
  /** When true, reference was detected by AI — shows a callout prompting the user to verify the metadata. */
  isAiReference?: boolean;
}

export interface BibliographyGeneratorDialogResult {
  key: string;
  exportFormat: 'auto' | 'custom';
  updatedReference?: Reference;
  rawCitationString?: string;
}

@Component({
  selector: 'sfo-bibliography-generator-dialog',
  templateUrl: './bibliography-generator-dialog.component.html',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    FormsModule,
    ReferenceEditorComponent,
    SfoWidgetModule,
  ],
})
export class BibliographyGeneratorDialogComponent {
  readonly dialogRef = inject(
    MatDialogRef<BibliographyGeneratorDialogComponent, BibliographyGeneratorDialogResult>,
  );
  readonly data = inject(MAT_DIALOG_DATA) as BibliographyGeneratorDialogData;

  readonly showEditor: boolean;
  private previousExportFormat: 'auto' | 'custom';

  /** True when the reference is AI or plain-text sourced — show "Reviewed" checkbox. */
  readonly showReviewedCheckbox = computed(() => {
    const csl = this.currentReference()?.csl as { _meta?: { source?: string } } | undefined;
    const source = csl?._meta?.source;
    return source === 'ai' || source === 'plain-text';
  });
  /** True when a promoted plain-text reference misses author/year needed for stable ordering in generated mode. */
  readonly showMissingPromotedFieldsPrompt = computed(() => {
    if (this.exportFormat() !== 'auto') {
      return false;
    }
    const csl = this.currentReference()?.csl as
      | (Reference['csl'] & {
          _meta?: { source?: string };
          author?: Array<unknown>;
          issued?: { 'date-parts'?: Array<Array<number | string>> };
        })
      | undefined;
    if (!csl || csl?._meta?.source !== 'plain-text') {
      return false;
    }
    const hasAuthor = Array.isArray(csl.author) && csl.author.length > 0;
    const firstYear = csl.issued?.['date-parts']?.[0]?.[0];
    const hasIssuedYear = typeof firstYear === 'number' && Number.isFinite(firstYear);
    return !hasAuthor || !hasIssuedYear;
  });

  exportFormat = signal<'auto' | 'custom'>(this.data.exportFormat);
  currentReference = signal<Reference>(this.data.reference);
  customText = signal('');
  canResetCustomText = computed(
    () => this.resolveOriginalRawString(this.currentReference()).length > 0,
  );

  constructor() {
    this.showEditor = this.data.canEdit && !this.data.isPlainReference;
    this.customText.set(this.resolveInitialCustomText(this.data.reference));
    this.previousExportFormat = this.exportFormat();

    effect(() => {
      const currentFormat = this.exportFormat();
      if (currentFormat === this.previousExportFormat) {
        return;
      }

      if (currentFormat === 'auto') {
        this.clearRawCitationFromCurrentReference();
      } else {
        this.customText.set(this.resolveCustomTextForFormatSwitch());
      }

      this.previousExportFormat = currentFormat;
    });
  }

  /**
   * Updates export format and keeps dialog state consistent between generated/custom modes.
   */
  onExportFormatChange(nextFormat: string): void {
    if (nextFormat !== 'auto' && nextFormat !== 'custom') {
      return;
    }

    if (this.exportFormat() === nextFormat) {
      return;
    }
    this.exportFormat.set(nextFormat);
  }

  /**
   * Stores live reference edits from the embedded reference editor.
   */
  onReferenceUpdate(updated: Reference): void {
    const previous = this.currentReference();
    const previousCsl = previous?.csl as
      | (Reference['csl'] & { _meta?: CSLReferenceMeta })
      | undefined;
    const nextCsl = updated?.csl as (Reference['csl'] & { _meta?: CSLReferenceMeta }) | undefined;
    if (!nextCsl) {
      this.currentReference.set(updated);
      return;
    }

    const mergedMeta = {
      ...(previousCsl?._meta || {}),
      ...(nextCsl._meta || {}),
    } as CSLReferenceMeta;
    const normalizedOriginalTextContent =
      mergedMeta.originalText ??
      (typeof mergedMeta.originalRawString === 'string' ? mergedMeta.originalRawString : undefined);
    const { originalRawString: _ignoredLegacyOriginalRawString, ...mergedMetaWithoutLegacy } =
      mergedMeta;
    const { originalRawString: _ignoredOriginalRawString, ...nextCslWithoutOriginalRawString } =
      nextCsl as Reference['csl'] & { originalRawString?: unknown };

    this.currentReference.set({
      ...updated,
      csl: {
        ...nextCslWithoutOriginalRawString,
        _meta: {
          ...mergedMetaWithoutLegacy,
          originalText: normalizedOriginalTextContent,
        },
      },
    });
  }

  /**
   * Marks AI/plain-text references as reviewed.
   */
  setReviewed(checked: boolean): void {
    this.currentReference.update((ref) => {
      const csl = ref?.csl as (Reference['csl'] & { _meta?: CSLReferenceMeta }) | undefined;
      if (!csl) {
        return { ...ref };
      }
      const nextMeta: CSLReferenceMeta = { ...csl._meta, reviewed: checked } as CSLReferenceMeta;
      return {
        ...ref,
        csl: { ...csl, _meta: nextMeta },
      };
    });
  }

  /**
   * Closes the dialog and returns updated export settings.
   */
  save(): void {
    const format = this.exportFormat();
    this.dialogRef.close({
      key: this.data.reference.key,
      exportFormat: format,
      updatedReference: this.buildUpdatedReference(),
      rawCitationString: format === 'custom' ? this.customText().trim() || undefined : undefined,
    });
  }

  /**
   * Builds the updated reference payload persisted by the caller.
   */
  private buildUpdatedReference(): Reference {
    const ref = this.currentReference();
    if (!ref.csl) {
      return ref;
    }

    const cslWithMeta = ref.csl as Reference['csl'] & {
      _meta?: CSLReferenceMeta;
      originalRawString?: unknown;
    };
    const { originalRawString: _ignoredLegacyOriginalRawString, ...metaWithoutLegacy } =
      (cslWithMeta._meta || {}) as CSLReferenceMeta;
    const normalizedOriginalTextContent =
      metaWithoutLegacy.originalText ??
      (typeof cslWithMeta._meta?.originalRawString === 'string'
        ? cslWithMeta._meta.originalRawString
        : undefined);
    const { originalRawString: _ignoredOriginalRawString, ...cslWithoutOriginalRawString } =
      cslWithMeta;
    const reviewed = ref.csl._meta?.reviewed === true;
    const nextMeta: CSLReferenceMeta = {
      ...metaWithoutLegacy,
      reviewed,
      originalText: normalizedOriginalTextContent,
    } as CSLReferenceMeta;
    const nextCsl: Reference['csl'] = {
      ...cslWithoutOriginalRawString,
      _meta: nextMeta,
      rawCitation:
        this.exportFormat() === 'auto' ? undefined : this.customText().trim() || undefined,
    };

    return {
      ...ref,
      csl: nextCsl,
    };
  }

  /**
   * Resolves initial custom text from reference metadata.
   */
  private resolveInitialCustomText(reference: Reference): string {
    const originalRawString = this.resolveOriginalRawString(reference);
    return String(reference?.csl?.rawCitation ?? originalRawString ?? reference?.bib ?? '').trim();
  }

  /**
   * Resolves custom text when switching into custom mode.
   */
  private resolveCustomTextForFormatSwitch(): string {
    const rawCitation = String(this.currentReference()?.csl?.rawCitation ?? '').trim();
    if (rawCitation.length > 0) {
      return rawCitation;
    }
    const originalRawString = this.resolveOriginalRawString(this.currentReference());
    if (originalRawString.length > 0) {
      return originalRawString;
    }
    const generatedBib = String(this.currentReference()?.bib ?? '').trim();
    if (generatedBib.length > 0) {
      return generatedBib;
    }
    return this.customText().trim();
  }

  resetCustomTextToDefault(): void {
    const originalRawString = this.resolveOriginalRawString(this.currentReference());
    if (!originalRawString) {
      return;
    }
    this.customText.set(originalRawString);
  }

  private resolveOriginalRawString(reference: Reference | undefined): string {
    const csl = reference?.csl as
      | (Reference['csl'] & {
          originalRawString?: unknown;
          _meta?: { originalTextContent?: unknown; originalRawString?: unknown };
        })
      | undefined;

    const originalRawString =
      csl?._meta?.originalText ?? csl?._meta?.originalRawString ?? csl?.originalRawString;
    if (typeof originalRawString === 'string') {
      return originalRawString;
    }
    return '';
  }

  /**
   * Clears rawCitation so generated mode always uses citeproc output.
   */
  private clearRawCitationFromCurrentReference(): void {
    this.currentReference.update((reference) => {
      if (!reference?.csl) {
        return reference;
      }
      return {
        ...reference,
        csl: {
          ...reference.csl,
          rawCitation: undefined,
        },
      };
    });
  }

  /**
   * Cancels dialog changes.
   */
  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
